/** Blocking "you're X minutes late — why?" form shown on the clock card until a reason is given. */
import React, { useState } from 'react';
import { AlertTriangle, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';

const REASONS = ['Traffic / travel', 'Appointment', 'Childcare / family', 'Overslept', 'Went to a site / job first', 'Agreed with manager', 'Other'];

export function LateReasonForm({ prompt, onSubmit }) {
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const needsDetail = reason === 'Other';
  const canSend = reason && (!needsDetail || detail.trim());
  const send = async () => { setBusy(true); try { await onSubmit({ reason, detail: detail.trim() || undefined }); } finally { setBusy(false); } };
  return (
    <div className="rounded-xl border-2 border-rose-300 bg-rose-50 p-4" data-testid="late-reason-form">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-rose-600 text-white flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-rose-900" data-testid="late-reason-title">You're {prompt.minutes} minute{prompt.minutes === 1 ? '' : 's'} late</div>
          <div className="text-xs text-rose-800/80 mt-0.5">Start time is {prompt.work_start}. Let your manager know the reason before you carry on.</div>
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="late-reason-options">
            {REASONS.map((r) => (
              <button key={r} type="button" onClick={() => setReason(r)} data-testid={`late-reason-${r.toLowerCase().replace(/[^a-z]+/g, '-')}`} aria-pressed={reason === r}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${reason === r ? 'bg-rose-700 border-rose-700 text-white' : 'bg-white border-rose-200 text-rose-900 hover:border-rose-400'}`}>{r}</button>
            ))}
          </div>
          <textarea value={detail} onChange={(e) => setDetail(e.target.value)} placeholder={needsDetail ? 'Please say what happened (required)' : 'Anything to add? (optional)'} rows={2}
            className="mt-3 w-full rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" data-testid="late-reason-detail" />
          <Button onClick={send} disabled={!canSend || busy} className="mt-2 w-full sm:w-auto bg-rose-700 hover:bg-rose-800 text-white h-10" data-testid="late-reason-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send reason
          </Button>
        </div>
      </div>
    </div>
  );
}
